// frontend/src/components/EmpresaPortal/StacksPersonalizados.js
//
// La empresa arma su propio stack combinando pruebas oficiales de PsyTest
// con sus propias pruebas personalizadas. El stack creado queda disponible
// de inmediato para invitar candidatos (ver backend: se habilita en
// empresa_stacks al crearlo).

import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X, Layers } from 'lucide-react';

const formVacio = () => ({ nombre: '', descripcion: '', dias_reaplicacion: '', pruebas: [] });
// Igual que MAX_PRUEBAS_POR_STACK en backend/src/services/creditosService.js
const MAX_PRUEBAS_POR_STACK = 3;

const StacksPersonalizados = ({ onCambio }) => {
  const [stacks, setStacks] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(formVacio());
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { cargarTodo(); }, []);

  const cargarTodo = async () => {
    setLoading(true);
    try {
      const [stacksRes, catalogoRes] = await Promise.all([
        fetch('/api/empresas/me/stacks-propios', { credentials: 'include' }),
        fetch('/api/empresas/me/catalogo-pruebas', { credentials: 'include' })
      ]);
      const stacksData = await stacksRes.json();
      const catalogoData = await catalogoRes.json();
      setStacks(Array.isArray(stacksData) ? stacksData : []);
      setCatalogo(Array.isArray(catalogoData) ? catalogoData : []);
    } catch (e) { /* listas quedan vacías */ }
    setLoading(false);
  };

  const abrirCrear = () => {
    setEditandoId(null);
    setForm(formVacio());
    setError('');
    setModalAbierto(true);
  };

  const abrirEditar = async (stack) => {
    try {
      const res = await fetch(`/api/empresas/me/stacks-propios/${stack.id}`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setEditandoId(stack.id);
      setForm({
        nombre: data.nombre,
        descripcion: data.descripcion || '',
        dias_reaplicacion: data.dias_reaplicacion ?? '',
        pruebas: data.pruebas.map(p => ({ prueba_id: p.prueba_id, orden: p.orden }))
      });
      setError('');
      setModalAbierto(true);
    } catch (e) {
      alert('No se pudo cargar el stack: ' + e.message);
    }
  };

  const cerrarModal = () => setModalAbierto(false);

  const togglePrueba = (pruebaId) => {
    setForm(f => {
      const yaEsta = f.pruebas.some(p => p.prueba_id === pruebaId);
      if (yaEsta) return { ...f, pruebas: f.pruebas.filter(p => p.prueba_id !== pruebaId) };
      if (f.pruebas.length >= MAX_PRUEBAS_POR_STACK) return f;
      return { ...f, pruebas: [...f.pruebas, { prueba_id: pruebaId, orden: f.pruebas.length }] };
    });
  };

  const actualizarOrden = (pruebaId, orden) => {
    setForm(f => ({ ...f, pruebas: f.pruebas.map(p => p.prueba_id === pruebaId ? { ...p, orden } : p) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.pruebas.length === 0) {
      setError('Selecciona al menos una prueba');
      return;
    }
    if (form.pruebas.length > MAX_PRUEBAS_POR_STACK) {
      setError(`Un stack puede tener máximo ${MAX_PRUEBAS_POR_STACK} pruebas`);
      return;
    }
    setGuardando(true);
    try {
      const url = editandoId ? `/api/empresas/me/stacks-propios/${editandoId}` : '/api/empresas/me/stacks-propios';
      const method = editandoId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          nombre: form.nombre,
          descripcion: form.descripcion,
          dias_reaplicacion: form.dias_reaplicacion === '' ? null : parseInt(form.dias_reaplicacion, 10),
          pruebas: form.pruebas
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'No se pudo guardar el stack');
      setModalAbierto(false);
      await cargarTodo();
      if (onCambio) onCambio();
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (stack) => {
    if (!window.confirm(`¿Eliminar el stack "${stack.nombre}"?`)) return;
    try {
      const res = await fetch(`/api/empresas/me/stacks-propios/${stack.id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      await cargarTodo();
      if (onCambio) onCambio();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="ep-card">
      <div className="ep-card-header">
        <h2>Mis stacks personalizados</h2>
        <button type="button" className="ep-btn-primary" onClick={abrirCrear}><Plus size={15} /> Crear stack</button>
      </div>
      <p className="ep-hint">
        Combina pruebas oficiales de PsyTest con tus propias pruebas para armar el stack exacto que necesitas.
      </p>

      {loading ? (
        <p className="ep-empty">Cargando...</p>
      ) : stacks.length === 0 ? (
        <div className="ep-empty-illustration">
          <div className="ep-empty-bubble">
            <Layers size={26} />
            <span className="ep-empty-bubble-badge"><Plus size={12} /></span>
          </div>
          <strong>Aún no has creado ningún stack propio.</strong>
          <p>Crea stacks para diferentes puestos o áreas de tu empresa.</p>
        </div>
      ) : (
        <div className="ep-table-wrap">
          <table className="ep-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Pruebas</th>
                <th>Reaplicación</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {stacks.map(s => (
                <tr key={s.id}>
                  <td>{s.nombre}</td>
                  <td>{s.total_pruebas}</td>
                  <td>{s.dias_reaplicacion ? `${s.dias_reaplicacion} días` : 'Sin límite'}</td>
                  <td>
                    <div className="ep-row-actions">
                      <button type="button" className="ep-btn-link" onClick={() => abrirEditar(s)}><Pencil size={13} /> Editar</button>
                      <button type="button" className="ep-btn-link ep-btn-link-danger" onClick={() => handleEliminar(s)}><Trash2 size={13} /> Eliminar</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalAbierto && (
        <div className="ep-modal-overlay">
          <div className="ep-modal">
            <div className="ep-modal-header">
              <h2>{editandoId ? 'Editar stack' : 'Crear stack'}</h2>
              <button type="button" className="ep-modal-close" onClick={cerrarModal}><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit} className="ep-modal-form">
              <div className="ep-modal-body">
                <div className="ep-field">
                  <label>Nombre del stack</label>
                  <input type="text" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} required />
                </div>
                <div className="ep-field">
                  <label>Descripción (opcional)</label>
                  <textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} rows={2} />
                </div>
                <div className="ep-field">
                  <label>Días de espera para reaplicar (opcional)</label>
                  <input type="number" min="1" value={form.dias_reaplicacion} onChange={(e) => setForm({ ...form, dias_reaplicacion: e.target.value })} placeholder="Vacío = sin límite" />
                </div>

                <div className="ep-field">
                  <label>Pruebas incluidas ({form.pruebas.length}/{MAX_PRUEBAS_POR_STACK})</label>
                  <small className="text-muted">
                    Máximo {MAX_PRUEBAS_POR_STACK} pruebas. Invitar a un stack cuesta 2 créditos; a una prueba individual, 1.
                  </small>
                  <div className="ep-catalogo-pruebas">
                    {catalogo.length === 0 ? (
                      <p className="text-muted">No hay pruebas disponibles.</p>
                    ) : (
                      catalogo.map(p => {
                        const seleccionada = form.pruebas.some(sel => sel.prueba_id === p.id);
                        const orden = form.pruebas.find(sel => sel.prueba_id === p.id)?.orden;
                        return (
                          <div key={p.id} className={`ep-catalogo-item ${seleccionada ? 'ep-catalogo-item-seleccionada' : ''}`}>
                            <label className="ep-checkbox-label">
                              <input
                                type="checkbox"
                                checked={seleccionada}
                                disabled={!seleccionada && form.pruebas.length >= MAX_PRUEBAS_POR_STACK}
                                onChange={() => togglePrueba(p.id)}
                              />
                              <span>{p.nombre}</span>
                              <span className={`ep-badge-origen ${p.es_oficial ? 'ep-badge-oficial' : 'ep-badge-propia'}`}>
                                {p.es_oficial ? 'Oficial' : 'Propia'}
                              </span>
                            </label>
                            {seleccionada && (
                              <input
                                type="number"
                                min="0"
                                className="ep-orden-input"
                                value={orden}
                                onChange={(e) => actualizarOrden(p.id, parseInt(e.target.value, 10))}
                                title="Orden dentro del stack"
                              />
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {error && <div className="ep-feedback ep-feedback-error">{error}</div>}
              </div>
              <div className="ep-modal-footer">
                <button type="button" className="ep-btn-secondary" onClick={cerrarModal} disabled={guardando}>Cancelar</button>
                <button type="submit" className="ep-btn-primary" disabled={guardando}>
                  {guardando ? 'Guardando...' : (editandoId ? 'Guardar cambios' : `Crear stack (${form.pruebas.length})`)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StacksPersonalizados;
